import React, { useCallback, useEffect, useState } from 'react';

import { v1 } from 'uuid';
import { AxiosError } from 'axios';

import { getMatchByPath } from '../infrastructure/utils/common';
import * as Service from '../services/ActivitiesService';
import { CityExploreForm } from '../components/forms/CityExploreForm';
import Error from '../components/stateMessage/Error';
import { getCity } from '../api/ActivitiesApi';
import { City } from '../types/City';

interface CityExploreProps {
  history: any;
  defaultCityName?: string;
}

const CityExplore = (props: CityExploreProps) => {
  const { history, defaultCityName } = props;

  const [cityName, setCityName] = useState(defaultCityName || null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isShownRadiusField, setIsShownRadiusField] = useState<boolean>(false);

  const onHandleShownRadiusFiled = () => {
    setIsShownRadiusField(prev => !prev);
  };

  const [radiusRequestState, setRadiusRequestState] = useState('idle');
  useEffect(() => {
    if (!defaultCityName) {
      const match = getMatchByPath(
        history.location.pathname,
        '/cities/explore/:city'
      );
      const { city } = match.params;
      setCityName(city);
    }
  }, [cityName, history.location.pathname, defaultCityName]);

  const [cityObj, setCityObj] = useState(null);

  const hideMessage = () => {
    setIsError(false);
  };

  const updateExploreConfiguration = async (city: City) => {
    try {
      const newCity = await Service.changeCityStatus(city);
      if (!defaultCityName) {
        initCityRadius(cityObj);
      }
      setCityObj(newCity);
    } catch (e) {
      setIsError(true);
      const error = e as AxiosError;
      setErrorMessage(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      return error;
    } finally {
      setTimeout(hideMessage, 5 * 1000);
    }
  };

  const updateCityExplorePicture = async (file, name: string) => {
    try {
      const guid = name.replace(/([^\w]+|\s+)/g, '') + v1();
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      const url = await Service.uploadCityImage(file, guid, 'explore');
      await updateExploreConfiguration({
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        ...cityObj,
        explorePicture: url,
      });
      return url;
    } catch (e) {
      setIsError(true);
      const error = e as AxiosError;
      setErrorMessage(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      return error;
    }
  };

  const exploreActions = {
    update: updateExploreConfiguration,
    updateCityExplorePicture,
  };

  const initCityRadius = useCallback(
    async cityObj => {
      setRadiusRequestState('in_progress');
      try {
        const { radius } = await getCity(cityObj.name);

        if (typeof radius === 'number') {
          setIsShownRadiusField(true);
        }
        if (cityObj) {
          setCityObj({ ...cityObj, radius });
        }
        setRadiusRequestState('success');
      } catch (error) {
        setRadiusRequestState('error');
        setIsError(true);
        setErrorMessage(
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          `Error code: ${e.response.status} \nError message: ${e.response.data}`
        );
      }
    },
    [cityName]
  );

  const isDefaultCity = cityName === '#default';

  useEffect(() => {
    async function fetchCity() {
      try {
        let cities = await Service.getCities();
        cities = cities.map(c => ({
          ...c,
          exploreConfigurations: c.exploreConfigurations || [],
        }));

        const city = cities.find(a => a.name === cityName);
        setCityObj(city);
        if (!isDefaultCity) {
          initCityRadius(city);
        }

        return city;
      } catch (e) {
        setIsError(true);
        setErrorMessage(
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          `Error code: ${e.response.status} \nError message: ${e.response.data}`
        );
        return e;
      }
    }
    if (cityName) fetchCity();
  }, [cityName, initCityRadius, isDefaultCity]);

  return (
    <>
      {isError && <Error text={errorMessage} />}
      <CityExploreForm
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        cityName={cityName}
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        cityObj={cityObj}
        history={history}
        isShownRadiusField={isShownRadiusField}
        radiusRequestState={radiusRequestState}
        onHandleShownRadiusFiled={onHandleShownRadiusFiled}
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        actions={exploreActions}
        isDefaultCity={isDefaultCity}
      />
    </>
  );
};

export default CityExplore;
